import {
  faBookOpenReader,
  faFileImport,
  faMicrophone,
  faMicrophoneSlash,
  faShare,
  faArrowUpRightFromSquare,
  faEarListen,
  faArrowRightFromBracket,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { parseJwt } from "../../../utils/parseJwt";
import Modal from "../MapModal";
import { RootState } from "../../../types";
import { useSelectedFile } from "../../../context/SelectedFileContext";
import "./ChatBar.css";
import VoiceChatModal from "./ModalVoice/VoiceChatModal";
import api from "../../../services/BackendServices";

const extractMermaidCode = (text) => {
  const mermaidRegex = /```mermaid([\s\S]*?)```/;
  const match = text.match(mermaidRegex);
  return match ? match[1].trim() : null;
};

function ChatBar({
  addChatMessage,
  handleReadFile,
  sessionId,
  fetchSessions,
  isLoadingResponse,
  setIsLoadingResponse,
}) {
  const USER_ID = parseJwt(
    useSelector((state: RootState) => state.userProfile.token)
  ).id;
  const username =
    useSelector((state: RootState) => state.userProfile.realm)
      ?.charAt(0)
      ?.toUpperCase() +
    useSelector((state: RootState) => state.userProfile.realm)?.slice(1);
  const maxFileSize = useSelector(
    (state: RootState) => state.userProfile.maxFileSize
  );
  const maxSize = maxFileSize * 1024 * 1024; // MB en bytes
  const { selectedFile, setSelectedFile } = useSelectedFile();

  const [fileName, setFileName] = useState("");
  const [userInput, setUserInput] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [isReadingDisabled, setIsReadingDisabled] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [dynamicMessage, setDynamicMessage] = useState(
    `Tamaño máximo: ${maxFileSize}MB`
  );
  const [isConceptMapDisabled, setIsConceptMapDisabled] = useState(false);
  const [conceptMap, setConceptMap] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false); // Estado para controlar el modal
  const [mermaidCode, setMermaidCode] = useState(""); // Estado para almacenar el código Mermaid
  const [isMoodalVoiceOpen, setIsMoodalVoiceOpen] = useState(false);

  const closeModal = () => setIsModalOpen(false);

  const toggleModalVoice = () => {
    setIsMoodalVoiceOpen((prev) => !prev);
  };

  useEffect(() => {
    if (selectedFile) {
      setFileName(selectedFile.name);
    } else {
      setFileName("");
    }
  }, [selectedFile]);

  useEffect(() => {
    if (!("webkitSpeechRecognition" in window)) {
      toast.error("Tu navegador no soporta la API de reconocimiento de voz.");
    } else {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recog = new SpeechRecognition();
      recog.continuous = true;
      recog.interimResults = true;
      recog.lang = "es-ES";

      recog.onstart = () => {
        setIsListening(true);
      };

      recog.onresult = (event) => {
        let interimTranscript = "";
        let finalTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        setUserInput(finalTranscript || interimTranscript);
      };

      recog.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
      };

      recog.onend = () => {
        setIsListening(false);
      };

      setRecognition(recog);
    }
  }, []);

  //Maneja la carga de archivos
  const handleFileChange = (event) => {
    setSelectedFile(null);
    setFileName("");
    setConceptMap("");
    setErrorMessage("");

    const file = event.target.files[0];

    event.target.value = "";

    if (!file) {
      toast.info("Por favor, selecciona un archivo.");
      return;
    }

    if (file.size > maxSize) {
      setErrorMessage("El archivo supera los 5MB.");
      setFileName("");
    } else {
      const formData = new FormData();
      formData.append("file", file);
      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoadingResponse) return;
    setIsLoadingResponse(true);
    setIsSubmitDisabled(true);
    if (!selectedFile && !userInput.trim()) {
      toast.error(
        "Por favor, selecciona un archivo o escribe algo para enviar."
      );
      setIsLoadingResponse(false);
      setIsSubmitDisabled(false);
      return;
    }

    addChatMessage(username, userInput);

    if (selectedFile) {
      const formData = new FormData();
      formData.append("file", selectedFile);

      try {
        // **Subir archivo**
        const uploadResponse = await api.GeneralServices.uploadFile(
          selectedFile,
          USER_ID
        );
        console.log("Archivo subido a S3 exitosamente:", uploadResponse.data);

        toast.info("Cargando archivo...");

        // **Procesar archivo**
        const response = await api.GeneralServices.processFile(
          selectedFile,
          sessionId,
          USER_ID
        );
        const result = response.data;
        addChatMessage(
          "Trixie",
          result.summary,
          result.summary,
          result.questions,
          result.qa_id
        );
        setFileName("");
        setSelectedFile(null);
        toast.success("Archivo procesado y resumido exitosamente.");
        setIsLoadingResponse(false);
      } catch (error) {
        setIsSubmitDisabled(false);
        console.error("Error:", error);
        toast.error("Ocurrió un error al procesar el archivo.");
      }
    }

    if (userInput.trim()) {
      try {
        const response = await api.GeneralServices.sendMessage(
          userInput,
          sessionId,
          USER_ID
        );
        const result = response.data;
        addChatMessage(
          "Trixie",
          result.response,
          result.response,
          result.questions,
          result.qa_id
        );
        setFileName("");
        setSelectedFile(null);
        setUserInput("");
        fetchSessions();
        setIsLoadingResponse(false);
      } catch (error) {
        setIsSubmitDisabled(false);
        console.error("Error:", error);
        toast.error("Ocurrió un error al enviar el mensaje.");
        setIsLoadingResponse(false);
      }
    }
    setIsSubmitDisabled(false);
  };

  const handleListen = () => {
    if (recognition) {
      if (isListening) {
        recognition.stop();
        toast.info("Reconocimiento de voz detenido.");
      } else {
        recognition.start();
        toast.info("Reconocimiento de voz iniciado.");
      }
    }
  };

  const handleReadFileClick = async () => {
    setIsReadingDisabled(true);
    if (selectedFile) {
      await handleReadFile(selectedFile);
      setIsReadingDisabled(false);
    } else {
      setIsReadingDisabled(false);
      toast.error("Por favor, selecciona un archivo.");
    }
  };

  const handleDeleteFile = () => {
    setFileName("");
    setSelectedFile(null);
  };

  useEffect(() => {
    if (userInput) {
      setDynamicMessage("Primero envíe el mensaje...");
    } else {
      setDynamicMessage(`Tamaño máximo: ${maxFileSize}MB`);
    }
  }, [userInput, maxFileSize]);

  const handleConceptMap = async () => {
    setIsConceptMapDisabled(true);

    if (!selectedFile) {
      toast.error("Por favor, selecciona un archivo.");
      setIsConceptMapDisabled(false);
      return;
    }

    try {
      const response = await api.GeneralServices.generateConceptMap(
        selectedFile,
        sessionId,
        USER_ID
      );

      const result = response.data;
      const extractedMermaidCode = extractMermaidCode(result.concept_map); // Extraer el código Mermaid

      if (extractedMermaidCode) {
        setMermaidCode(extractedMermaidCode); // Almacenar el código Mermaid en el estado
        setUserInput("");
        addChatMessage(
          "Trixie",
          "Mapa conceptual generado correctamente.", // Mensaje amigable para el usuario
          "Mapa conceptual generado correctamente.",
          [],
          result.qa_id
        );
      } else {
        throw new Error("No se pudo extraer el código Mermaid.");
      }

      // Limpiar el archivo seleccionado después de generar el mapa conceptual
      setSelectedFile(null);
      setFileName("");

      toast.success("Mapa conceptual generado exitosamente.");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Ocurrió un error al generar el mapa conceptual.");
    } finally {
      setIsConceptMapDisabled(false); // Reiniciar el estado del botón
    }
  };

  const openInNewTab = () => {
    if (!mermaidCode) {
      toast.error("No hay código Mermaid disponible.");
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Mapa Conceptual</title>
        <script src="https://cdn.jsdelivr.net/npm/mermaid/dist/mermaid.min.js"></script>
        <style>
          body { margin: 0; padding: 20px; }
          .mermaid { width: 100%; height: 100%; }
        </style>
      </head>
      <body>
        <div class="mermaid">
          ${mermaidCode}
        </div>
        <script>
          mermaid.initialize({ startOnLoad: true });
        </script>
      </body>
      </html>
    `;

    const newWindow = window.open();

    if (newWindow) {
      newWindow.document.write(htmlContent);
      newWindow.document.close();
    } else {
      toast.error("No se pudo abrir una nueva ventana.");
    }
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setUserInput(value);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";

      const scrollHeight = textareaRef.current.scrollHeight;
      const minHeight = 20; // altura base
      const maxHeight = 90;

      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  return (
    <form className="chat-bar" onSubmit={handleSubmit}>
      <div className="chat-input-wrapper">
        {selectedFile && (
          <div className="file-preview">
            <span>[📄{fileName} ]</span>
            <button
              type="button"
              onClick={handleDeleteFile}
              className="delete-file"
            >
              &times;
            </button>
          </div>
        )}

        <textarea
          ref={textareaRef}
          className="chat-input"
          placeholder={
            selectedFile
              ? "Envía el archivo antes de escribir un mensaje..."
              : "Escribe tu mensaje..."
          }
          value={userInput}
          onChange={handleInputChange}
          disabled={!!selectedFile}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          rows={1}
        />

        <div className="chat-input-actions left-aligned">
          <button type="button" onClick={handleListen} className="action-btn">
            <FontAwesomeIcon
              icon={isListening ? faMicrophoneSlash : faMicrophone}
            />
            <span>{isListening ? "Detener" : "Transcribir"}</span>
          </button>

          <label htmlFor="fileInput" className="action-btn file-btn">
            <FontAwesomeIcon icon={faFileImport} />
            <span>Subir Archivo</span>
            <input
              type="file"
              id="fileInput"
              name="file"
              accept=".pdf,.docx,.txt"
              onChange={handleFileChange}
              disabled={userInput !== ""}
              hidden
            />
          </label>

          {!selectedFile && (
            <button
              type={userInput ? "submit" : "button"}
              className="send-btn action-btn"
              onClick={userInput ? undefined : toggleModalVoice}
              disabled={
                isSubmitDisabled ||
                isLoadingResponse ||
                (!userInput && selectedFile)
              }
            >
              {userInput ? (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} className="send-icon" />
                  <span>Enviar</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faEarListen} />
                  <span>Chat de Voz</span>
                </>
              )}
            </button>
          )}

          {selectedFile && (
            <>
              <button
                type="submit"
                className="action-btn"
                disabled={isSubmitDisabled}
              >
                <FontAwesomeIcon icon={faShare} />
                <span>Resumir</span>
              </button>

              <button
                type="button"
                onClick={handleConceptMap}
                disabled={isConceptMapDisabled}
                className="action-btn"
              >
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                <span>Mapa</span>
              </button>

              <button
                className="action-btn"
                type="button"
                onClick={handleReadFileClick}
                disabled={isReadingDisabled}
              >
                <FontAwesomeIcon icon={faBookOpenReader} />
                <span>Leer</span>
              </button>
            </>
          )}
        </div>
      </div>

      {mermaidCode && (
        <div className="concept-map-actions">
          <button
            type="button"
            onClick={() => openInNewTab()}
            className="btn-concept-map"
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            <span className="button-text"> Ver Mapa</span>
          </button>
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div className="mermaid">{mermaidCode}</div>
      </Modal>

      <VoiceChatModal
        isOpen={isMoodalVoiceOpen}
        onClose={toggleModalVoice}
        addChatMessage={addChatMessage}
        sessionId={sessionId}
        username={username}
        userId={USER_ID}
      />
    </form>
  );
}

export default ChatBar;
